import { cx } from '@linaria/core';
import { useRef, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { getCaptionValue } from '@pm2/content-engine';
import { ElementToolbar } from '@pm2/element-toolbar';
import { pm2ConfigStore, usePM2DisplayContext } from '@pm2/store';
import { SizeTransition } from '@utils/react';
import { Bullets } from './atoms/Bullets.js';
import { Chevrons } from './atoms/Chevrons.js';
import { Scrambled } from './atoms/Scrambled.js';
import { Slide } from './atoms/Slide.js';
import { Caption } from './atoms/caption.slider.js';
import { useDragSlide } from './hooks/useDragSlide.js';
import { header, headerText, heading, elementToolbar, sliderWrapper, sliderContainer, slider, imageCaptionWrapper, CAPTION_TRANSITION_DURATION_MS, imageCaptionTransition, container } from './slider.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

const SliderComponentSelector = "slider-component";
let ImageStates = /*#__PURE__*/function (ImageStates) {
  ImageStates[ImageStates["NO_IMAGE_LOADED"] = 0] = "NO_IMAGE_LOADED";
  ImageStates[ImageStates["SUCCESS"] = 1] = "SUCCESS";
  ImageStates[ImageStates["ERROR"] = 2] = "ERROR";
  return ImageStates;
}({});
function SliderComponent(props) {
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const {
    inLightbox
  } = usePM2DisplayContext();
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [, dragProps] = useDragSlide(sliderRef, {
    amountOfSlides: props.slides.length,
    index,
    onChange: setIndex
  });
  const currentSlide = props.slides[index];
  const aspectRatio = currentSlide ? currentSlide.image.height / currentSlide.image.width : 0.5625;
  if (props.slides.length === 0) {
    // unable to render completely empty slides; they don't really make any
    // sense either so assume that they work
    return null;
  }
  if (props.pm2.isScrambled) {
    return /*#__PURE__*/jsx(Scrambled, {});
  }
  const displayCaption = props.options.displayCaption || inLightbox;
  const firstSlide = props.slides?.[props.slides.length - 1];
  const lastSlide = props.slides?.[0];
  return /*#__PURE__*/jsxs("section", {
    ref: containerRef,
    className: cx("gSlider", container, inLightbox && "inLightbox", audience === "Grade1To3" && "simple"),
    "data-component-type": SliderComponentSelector,
    children: [/*#__PURE__*/jsxs("header", {
      className: header,
      children: [/*#__PURE__*/jsxs("div", {
        className: headerText,
        children: [props.options.displayTitle && /*#__PURE__*/jsx("h4", {
          className: heading,
          children: getCaptionValue(props.meta.caption.title)
        }), displayCaption && /*#__PURE__*/jsx(Caption, {
          type: "slider",
          caption: props.meta.caption,
          inLightbox: inLightbox,
          includeTitle: false
        })]
      }), /*#__PURE__*/jsx("div", {
        className: elementToolbar,
        children: /*#__PURE__*/jsx(ElementToolbar, {
          node: props,
          customPosition: true
        })
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: sliderWrapper,
      children: [/*#__PURE__*/jsx("div", {
        ...dragProps,
        className: sliderContainer,
        style: {
          paddingBottom: aspectRatio ? `${aspectRatio * 100}%` : undefined
        },
        children: /*#__PURE__*/jsxs("div", {
          ref: sliderRef,
          className: slider,
          style: {
            transform: `translateX(-${index * 100}%)`
          },
          children: [firstSlide && /*#__PURE__*/jsx(Slide, {
            index: -1,
            model: firstSlide,
            isCurrent: false
          }), props.slides?.map((slide, index) => /*#__PURE__*/jsx(Slide, {
            index: index,
            model: slide,
            isCurrent: currentSlide === slide
          }, index)), lastSlide && /*#__PURE__*/jsx(Slide, {
            index: props.slides.length,
            model: lastSlide,
            isCurrent: false
          })]
        })
      }), props.slides.length > 1 && /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(Chevrons, {
          onPrevClick: () => setIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : props.slides.length - 1),
          onNextClick: () => setIndex(prevIndex => (prevIndex + 1) % props.slides.length)
        }), /*#__PURE__*/jsx(Bullets, {
          slides: props.slides,
          activeIndex: index,
          bulletType: props.options.bulletType ?? "square",
          onBulletClick: setIndex
        })]
      })]
    }), displayCaption && /*#__PURE__*/jsx(TransitionGroup, {
      className: imageCaptionWrapper,
      children: /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        swappable: true,
        classNames: imageCaptionTransition,
        timeout: CAPTION_TRANSITION_DURATION_MS,
        children: ref => currentSlide?.caption ? /*#__PURE__*/jsx(Caption, {
          ref: ref,
          type: "image",
          includeTitle: true,
          inLightbox: inLightbox,
          caption: currentSlide.caption
        }) : /*#__PURE__*/jsx("div", {
          ref: ref
        })
      }, index)
    })]
  });
}

export { ImageStates, SliderComponent, SliderComponentSelector };
//# sourceMappingURL=slider.js.map
